import { FormEvent, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import {
  BellIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  FolderIcon,
  HomeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { BARUK_LOGO_URL, BARUK_MARK_URL } from '../../constants/productBranding';
import { PortalHeader } from './PortalHeader';
import { PortalStateProvider } from './PortalState';

const navItems = [
  { to: '/portal', label: 'Início', icon: HomeIcon, end: true },
  { to: '/portal/projects', label: 'Meus Projetos', icon: FolderIcon, end: false },
  { to: '/portal/reports', label: 'Relatórios', icon: ChartBarIcon, end: false },
  { to: '/portal/documents', label: 'Documentos', icon: DocumentTextIcon, end: false },
  { to: '/portal/conversations', label: 'Conversas', icon: ChatBubbleLeftRightIcon, end: false },
  { to: '/portal/requests', label: 'Solicitações', icon: ClipboardDocumentCheckIcon, end: false },
];

const bottomNavItems = [
  { to: '/portal/notifications', label: 'Notificações', icon: BellIcon, end: false, badge: 3 },
  { to: '/portal/account', label: 'Minha Conta', icon: UserIcon, end: false },
];

function PortalFrame() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const collapseLabel = collapsed ? 'Expandir menu' : 'Recolher menu';
  const brandRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const header = document.querySelector('header');
    const brand = brandRef.current;
    if (!header || !brand) return;

    const desktop = window.matchMedia('(min-width: 768px)');
    const apply = () => {
      const factor = collapsed && desktop.matches ? 1 : 2;
      brand.style.height = `${header.getBoundingClientRect().height * factor}px`;
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(header);
    desktop.addEventListener('change', apply);
    return () => {
      observer.disconnect();
      desktop.removeEventListener('change', apply);
    };
  }, [collapsed]);

  return (
    <div className="flex h-full min-h-0 bg-baruk-surface text-secondary-900">
      <aside
        className={`${menuOpen ? 'flex' : 'hidden'} absolute inset-y-0 z-20 w-64 flex-col border-r border-secondary-200 bg-white md:static md:flex ${
          collapsed ? 'md:w-[4.5rem]' : 'md:w-64'
        }`}
      >
        <div
          ref={brandRef}
          className={`flex shrink-0 items-center justify-center bg-primary-500 ${collapsed ? 'px-2 md:px-2' : 'px-4'}`}
        >
          <Link
            to="/portal"
            aria-label="Baruk"
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-center"
          >
            <img
              src={BARUK_LOGO_URL}
              alt=""
              className={`h-12 w-auto max-w-full object-contain ${collapsed ? 'md:hidden' : ''}`}
            />
            {collapsed ? (
              <img src={BARUK_MARK_URL} alt="" className="hidden h-10 w-10 object-contain md:block" />
            ) : null}
          </Link>
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={item.label}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center rounded-xl py-2 text-sm font-medium ${
                    collapsed ? 'justify-between px-3 md:justify-center md:px-2' : 'justify-between px-3'
                  } ${
                    isActive
                      ? 'bg-primary-50 text-primary-800'
                      : 'text-secondary-700 hover:bg-secondary-100'
                  }`
                }
              >
                <span className={`flex items-center ${collapsed ? 'gap-2 md:gap-0' : 'gap-2'}`}>
                  <Icon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className={collapsed ? 'md:sr-only' : ''}>{item.label}</span>
                </span>
              </NavLink>
            );
          })}
        </nav>
        <div className={`border-t border-secondary-200 p-2 ${collapsed ? 'md:px-2' : 'px-3'}`}>
          <div className="mb-1 flex flex-col gap-1">
            {bottomNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={item.label}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center rounded-xl py-2 text-sm font-medium ${
                      collapsed ? 'justify-between px-3 md:justify-center md:px-2' : 'justify-between px-3'
                    } ${
                      isActive
                        ? 'bg-primary-50 text-primary-800'
                        : 'text-secondary-700 hover:bg-secondary-100'
                    }`
                  }
                >
                  <span className={`flex items-center ${collapsed ? 'gap-2 md:gap-0' : 'gap-2'}`}>
                    <span className="relative">
                      <Icon className="h-5 w-5 shrink-0" aria-hidden />
                      {'badge' in item && item.badge && collapsed ? (
                        <span className="absolute -right-1.5 -top-1 hidden h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white md:flex">
                          {item.badge}
                        </span>
                      ) : null}
                    </span>
                    <span className={collapsed ? 'md:sr-only' : ''}>{item.label}</span>
                  </span>
                  {'badge' in item && item.badge ? (
                    <span className={`flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white ${collapsed ? 'md:hidden' : ''}`}>
                      {item.badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setCollapsed((current) => !current)}
            title={collapseLabel}
            aria-label={collapseLabel}
            aria-expanded={!collapsed}
            className={`flex w-full items-center rounded-xl py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-100 ${
              collapsed ? 'justify-between gap-2 px-3 md:justify-center md:px-2' : 'gap-2 px-3'
            }`}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="hidden h-5 w-5 shrink-0 md:block" aria-hidden />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5 shrink-0" aria-hidden />
            )}
            {collapsed ? <ChevronDoubleLeftIcon className="h-5 w-5 shrink-0 md:hidden" aria-hidden /> : null}
            <span className={collapsed ? 'md:sr-only' : ''}>Recolher</span>
          </button>
        </div>
      </aside>
      {menuOpen ? (
        <button
          type="button"
          className="absolute inset-0 z-10 bg-secondary-900/30 md:hidden"
          aria-label="Fechar menu"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PortalHeader onOpenMenu={() => setMenuOpen(true)} />
        <main className="min-h-0 flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PortalLayout() {
  return (
    <PortalStateProvider>
      <PortalFrame />
    </PortalStateProvider>
  );
}

export function PortalCommentForm({
  onSubmit,
}: {
  onSubmit: (body: string) => void;
}) {
  const [body, setBody] = useState('');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    onSubmit(body);
    setBody('');
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <label className="erp-label" htmlFor="portal-comment">
        Sua mensagem
      </label>
      <textarea
        id="portal-comment"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={3}
        className="w-full border border-secondary-300 px-3 py-2 text-sm"
        placeholder="Escreva para a equipe do projeto"
      />
      <button type="submit" className="erp-btn-primary erp-btn-sm" disabled={!body.trim()}>
        Enviar
      </button>
    </form>
  );
}
